import React, { useState } from 'react';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import { apiLogin, getAdminToken, setAdminSession } from '../lib/api';
import { canAccessPath, defaultHomePath } from '../lib/access';

const FEATURES = [
  'Skill registry & lifecycle governance',
  'Agent profiles, runs, and routing',
  'Approvals, audit logs, and compliance',
  'Integrations registry & health checks',
];

function resolvePostLoginPath(fromPath) {
  if (fromPath && fromPath !== '/login' && canAccessPath(fromPath)) {
    return fromPath;
  }
  return defaultHomePath();
}

function Login() {
  const location = useLocation();
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (getAdminToken()) {
    return <Redirect to={resolvePostLoginPath(location.state?.from?.pathname)} />;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!email.trim() || !password) {
      setError('Enter your work email and password.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const data = await apiLogin(email.trim(), password);
      setAdminSession(data.token, data.user.role, data.user);
      history.replace(resolvePostLoginPath(location.state?.from?.pathname));
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-brand" aria-label="Product overview">
        <div className="login-brand__inner">
          <div className="login-brand__mark" aria-hidden="true">
            ECS
          </div>
          <p className="login-brand__eyebrow">Enterprise Claude Skills Platform</p>
          <h1 className="login-brand__title">Multi-tenant AI Control Plane</h1>
          <p className="login-brand__tagline">
            Govern Skills • Agents • Approvals • Integrations • Governance
          </p>
          <ul className="login-brand__features">
            {FEATURES.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className="login-brand__footer">
            Secure enterprise operations console for licensing, metering, routing, and audit.
          </p>
        </div>
      </section>

      <section className="login-panel">
        <div className="login-card">
          <Link to="/" className="login-card__back">← Back to homepage</Link>
          <header className="login-card__header">
            <h2>Sign in</h2>
            <p>Use your work email and password to access the control plane.</p>
          </header>

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <div className="login-field">
              <label htmlFor="email">Work email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError('');
                }}
                className="login-input"
                placeholder="you@company.com"
                autoComplete="username"
                autoFocus
                disabled={submitting}
              />
            </div>

            <div className="login-field">
              <label htmlFor="password">Password</label>
              <div className="login-input-wrap">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError('');
                  }}
                  className="login-input"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="login-input-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {error && (
              <p className="login-alert login-alert--error" role="alert">
                {error}
              </p>
            )}

            <button type="submit" className="login-submit" disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="login-card__legal">
            Access is role-based (Admin / Operator / Viewer). Your role is assigned by an administrator — it is not selectable at login.
          </p>
        </div>
      </section>
    </main>
  );
}

export default Login;
